import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { adminUpsertSocialPost } from "@/lib/social-admin.functions";
import { listGhlSocialAccounts } from "@/lib/ghl-connect.functions";
import { publishSocialPostViaGhl } from "@/lib/social-analytics.functions";
import { SocialSubNav } from "@/components/admin/SocialSubNav";
import { MediaLightbox, socialMediaUrls } from "@/components/social/SocialPreview";
import { PlatformPreviews } from "@/components/social/PlatformPreviews";
import { ComposeAiTools } from "@/components/social/ComposeAiTools";
import { BlogRepurposePicker } from "@/components/social/BlogRepurposePicker";
import { RecommendedTimes } from "@/components/social/RecommendedTimes";
import { SOCIAL_LINK_PATHS, socialUrl } from "@/lib/social-links";
import { runSocialComplianceCheck } from "@/lib/social-compliance-guard";
import { ensureAdvertiserIdentification } from "@/lib/compliance-advertising";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, CalendarClock, CheckCircle2, PlugZap, Send } from "lucide-react";
import { toast } from "sonner";
import { etDateTimeLocalToDate, etDateTimeLocalToIso } from "@/lib/et-datetime";

const PLATFORMS = ["facebook", "instagram", "linkedin", "x", "youtube", "tiktok"] as const;

export const Route = createFileRoute("/admin/social/compose")({
  component: Compose,
});

function Compose() {
  const upsert = useServerFn(adminUpsertSocialPost);
  const listAccounts = useServerFn(listGhlSocialAccounts);
  const publishViaGhl = useServerFn(publishSocialPostViaGhl);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const accountsQuery = useQuery({
    queryKey: ["ghl-social-accounts"],
    queryFn: () => listAccounts(),
  });
  const accounts = accountsQuery.data?.ok ? accountsQuery.data.accounts : [];

  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(["linkedin", "facebook"]);
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [body, setBody] = useState("");
  const [linkUrl, setLinkUrl] = useState("https://dscrloanmarket.com/dscr-calculator");
  const [media, setMedia] = useState<string[]>([]);
  const [scheduledAt, setScheduledAt] = useState("");
  const [isEvergreen, setIsEvergreen] = useState(false);
  // Only the clicked action goes busy; the other buttons stay idle (but locked).
  const [savingAction, setSavingAction] = useState<"draft" | "schedule-ghl" | "post-ghl" | null>(null);
  const saving = savingAction !== null;
  const [lightbox, setLightbox] = useState<{ images: string[]; index: number } | null>(null);

  const togglePlatform = (platform: string) =>
    setSelectedPlatforms((prev) =>
      prev.includes(platform) ? prev.filter((item) => item !== platform) : [...prev, platform],
    );
  const toggleAccount = (id: string) =>
    setSelectedAccounts((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );

  // The licensee name + NMLS line is appended automatically on save, so the
  // preview and the compliance check both run against the final copy.
  const previewBody = ensureAdvertiserIdentification(
    linkUrl && !body.includes(linkUrl) ? `${body}\n\n${linkUrl}` : body,
  );
  const compliance = useMemo(
    () => runSocialComplianceCheck({ body: previewBody, link_url: linkUrl || null, media_urls: media }),
    [previewBody, linkUrl, media],
  );

  const save = async (action: "draft" | "schedule-ghl" | "post-ghl") => {
    if (savingAction) return;
    if (!body.trim()) return toast.error("Write something first");
    if (selectedPlatforms.length === 0) return toast.error("Pick at least one platform");
    if (action === "schedule-ghl" && !scheduledAt) return toast.error("Pick a schedule date");
    if (action === "schedule-ghl" && (etDateTimeLocalToDate(scheduledAt)?.getTime() ?? 0) <= Date.now())
      return toast.error("Pick a schedule date in the future");
    if (action !== "draft" && selectedAccounts.length === 0)
      return toast.error("Select at least one GHL account");
    // Drafts are work-in-progress: never block saving one. Compliance errors
    // still block anything that actually goes out (schedule / post now).
    if (action !== "draft" && compliance.errors.length > 0)
      return toast.error(`Blocked by compliance: ${compliance.errors[0].label}`);
    setSavingAction(action);
    try {
      const finalBody = linkUrl && !body.includes(linkUrl) ? `${body}\n\n${linkUrl}` : body;
      const row = await upsert({
        data: {
          platforms: selectedPlatforms,
          body: finalBody,
          link_url: linkUrl || null,
          media_urls: media,
          external_ids: {
            target_account_ids: selectedAccounts,
            source: "manual_compose",
            is_evergreen: isEvergreen,
          },
          status: "draft",
          scheduled_at: etDateTimeLocalToIso(scheduledAt),
        },
      });
      if (action !== "draft") {
        await publishViaGhl({
          data: {
            post_id: (row as any).id,
            ghl_account_ids: selectedAccounts,
            schedule_at:
              action === "schedule-ghl" ? etDateTimeLocalToIso(scheduledAt) : null,
          },
        });
      }
      toast.success(
        action === "draft"
          ? "Draft saved"
          : action === "schedule-ghl"
            ? "Scheduled in GHL"
            : "Sent to GHL for publishing",
      );
      await queryClient.invalidateQueries({ queryKey: ["social-posts"] });
      void navigate({ to: action === "draft" ? "/admin/social/drafts" : "/admin/social/calendar" });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Save failed");
    } finally {
      setSavingAction(null);
    }
  };

  

  return (
    <div>
      <SocialSubNav />
      <h1 className="font-display text-2xl font-bold text-primary">Compose post</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Draft with AI, generate a branded image, upload media, or recreate an original post from any
        URL — all output passes the reverse-mortgage compliance guardrails.
      </p>
      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_360px]">
        <div className="space-y-5">
          <div>
            <Label>Platforms</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {PLATFORMS.map((platform) => (
                <Button
                  key={platform}
                  type="button"
                  variant={selectedPlatforms.includes(platform) ? "default" : "outline"}
                  size="sm"
                  onClick={() => togglePlatform(platform)}
                  className="capitalize"
                >
                  {platform}
                </Button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="body">Post content</Label>
            <Textarea
              id="body"
              rows={9}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              placeholder="What do you want to share?"
              maxLength={5000}
            />
            <p className="mt-1 text-xs text-muted-foreground">{previewBody.length} characters</p>
          </div>

          <ComposeAiTools
            content={body}
            setContent={setBody}
            media={media}
            setMedia={(updater) => setMedia(updater)}
          />

          <BlogRepurposePicker
            onImport={(text: string, mediaUrl: string | null, link: string | null) => {
              setBody(text);
              if (link) setLinkUrl(link);
              if (mediaUrl) setMedia((prev) => [...prev, mediaUrl]);
            }}
          />



          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="link">CTA link</Label>
              <select
                id="link-picker"
                aria-label="Pick a vetted page"
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={SOCIAL_LINK_PATHS.some((p) => socialUrl(p) === linkUrl) ? linkUrl : ""}
                onChange={(event) => setLinkUrl(event.target.value)}
              >
                <option value="">Custom / blog URL…</option>
                {SOCIAL_LINK_PATHS.map((path) => (
                  <option key={path} value={socialUrl(path)}>
                    {path}
                  </option>
                ))}
              </select>
              <Input
                id="link"
                className="mt-2"
                value={linkUrl}
                onChange={(event) => setLinkUrl(event.target.value)}
                placeholder="https://dscrloanmarket.com/dscr-calculator"
              />
            </div>
            <div>
              <Label htmlFor="sched">Schedule for (Eastern Time)</Label>
              <Input
                id="sched"
                type="datetime-local"
                value={scheduledAt}
                onChange={(event) => setScheduledAt(event.target.value)}
              />
            </div>
          </div>

          <RecommendedTimes value={scheduledAt} onPick={setScheduledAt} />

          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 accent-primary"
              checked={isEvergreen}
              onChange={(e) => setIsEvergreen(e.target.checked)}
            />
            Mark as evergreen (safe to reuse later)
          </label>

          <div>
            <Label className="flex items-center gap-1">
              <PlugZap className="h-4 w-4" /> Publish to GHL accounts
            </Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {accounts.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No GHL social accounts loaded. Connect GHL first.
                </p>
              ) : (
                accounts.map((account: any) => (
                  <Button
                    key={account.id}
                    type="button"
                    size="sm"
                    variant={selectedAccounts.includes(account.id) ? "default" : "outline"}
                    onClick={() => toggleAccount(account.id)}
                  >
                    {account.platform} · {account.name}
                  </Button>
                ))
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={saving}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                void save("draft");
              }}
            >
              {savingAction === "draft" ? "Saving…" : "Save draft"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={saving}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                void save("schedule-ghl");
              }}
            >
              <CalendarClock className="mr-2 h-4 w-4" />{" "}
              {savingAction === "schedule-ghl" ? "Scheduling…" : "Schedule in GHL"}
            </Button>
            <Button
              type="button"
              disabled={saving}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                void save("post-ghl");
              }}
            >
              <Send className="mr-2 h-4 w-4" /> {savingAction === "post-ghl" ? "Posting…" : "Post now"}
            </Button>
          </div>
        </div>

        <aside className="space-y-3">
          <PlatformPreviews
            body={previewBody}
            mediaUrls={media}
            linkUrl={linkUrl || null}
            platforms={selectedPlatforms}
            onOpenMedia={(index) => setLightbox({ images: media, index })}
          />
          <div
            className={`rounded-lg border p-3 text-xs ${
              compliance.errors.length > 0
                ? "border-destructive/40 bg-destructive/5"
                : compliance.warnings.length > 0
                  ? "border-amber-400/50 bg-amber-50/60 dark:bg-amber-500/10"
                  : "border-border bg-muted/40"
            }`}
          >
            <p className="flex items-center gap-1.5 font-semibold">
              {compliance.errors.length > 0 || compliance.warnings.length > 0 ? (
                <AlertTriangle className="h-3.5 w-3.5" />
              ) : (
                <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
              )}
              Compliance:{" "}
              {compliance.errors.length > 0
                ? "blocked"
                : compliance.warnings.length > 0
                  ? "review"
                  : "clean"}
            </p>
            {compliance.findings.length > 0 ? (
              <ul className="mt-2 space-y-1">
                {compliance.findings.slice(0, 6).map((finding, index) => (
                  <li key={`${finding.ruleId ?? finding.label}-${index}`}>
                    <span
                      className={
                        finding.severity === "error"
                          ? "font-semibold text-destructive"
                          : "font-semibold text-amber-700 dark:text-amber-400"
                      }
                    >
                      {finding.severity === "error" ? "Blocking" : "Warning"}:
                    </span>{" "}
                    {finding.label}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1 text-muted-foreground">
                No lexicon, net-impression, or link issues found.
              </p>
            )}
          </div>
          <p className="rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
            Drafts stay editable. Scheduling or posting sends the saved post to selected GHL
            accounts.
          </p>
        </aside>
      </div>
      {lightbox && (
        <MediaLightbox
          images={lightbox.images}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
          onIndex={(index) =>
            setLightbox((state) => (state ? { ...state, index } : state))
          }
        />
      )}
    </div>
  );
}
